import { useNavigate } from "react-router-dom";
import "react-lazy-load-image-component/src/effects/blur.css";
import LogoComponent from "../Reusable/LogoComponent";
import LogoDark from "../../assets/images/logo-dark.png";
import LogoLight from "../../assets/images/logo-light.png";

// Import Social Icons
import { FaInstagram, FaFacebookF } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { useSelector } from "react-redux";

// Import the them context
import { useTheme } from "../../context/ThemeContext";

const Footer = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const currentYear = new Date().getFullYear();

  const handleNavigation = (link) => {
    if (link.startsWith("http://") || link.startsWith("https://")) {
      window.open(link, "_blank");
    } else {
      navigate(link);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const socialIcons = [
    {
      icon: <FaInstagram />,
      alt: "instagram",
      link: "https://www.instagram.com",
    },
    {
      icon: <FaFacebookF />,
      alt: "facebook",
      link: "https://www.facebook.com",
    },
    { icon: <FaXTwitter />, alt: "twitter", link: "https://x.com" },
  ];

  const footerLinks = [
    {
      title: "Quick Links",
      links: [
        { name: "Home", path: "/" },
        { name: "About Us", path: "https://icieos.com/about" },
        { name: "Contact Us", path: "https://icieos.com/contact" },
      ]
    },
    {
      title: "Terms & Conditions",
      links: [
        { name: "Introduction", path: "/terms-and-conditions#introduction" },
        {
          name: "User Rights",
          path: "/terms-and-conditions#user-responsibilities",
        },
        { name: "Payments", path: "/terms-and-conditions#payments-escrow" },
        {
          name: "Liabilities",
          path: "/terms-and-conditions#limitation-liability",
        },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", path: "/privacy-policy#introduction" },
        { name: "Cookie Policy", path: "/privacy-policy#cookies" },
        { name: "Referral Program", path: "/privacy-policy#referral-program" },
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-[1440px] px-6 md:px-8 w-full">
      <footer
        className="w-full border-t"
        style={{
          borderTopColor: "var(--color-stroke-01)",
          padding: "48px 0 0 0",
        }}
      >
        <div className="flex flex-col lg:flex-row justify-between items-start gap-10 lg:gap-8">
          {/* Logo + tagline + socials */}
          <div className="flex flex-col justify-start items-center lg:items-start lg:w-[25%] lg:max-w-[400px]">
            <div className="mb-6 -ml-3 lg:-ml-4">
              <LogoComponent
                src={theme === "dark" ? LogoDark : LogoLight}
                alt="icieos Logo"
                link="/"
                className="w-24 sm:w-28 md:w-32 lg:w-36"
              />
            </div>
            <p
              className="mb-6 text-center lg:text-left text-footer-level-1 leading-relaxed"
              style={{ color: "var(--color-footer-01)" }}
            >
              Empowering SMEs with cutting-edge engineering and IT solutions
              across diverse industries,
            </p>
            <div className="flex space-x-4">
              {socialIcons.map((s, idx) => (
                <div
                  key={idx}
                  onClick={() => handleNavigation(s.link)}
                  className="
                  group cursor-pointer 
                  border border-[var(--color-footer-02)] p-[9px] rounded-full
                  transition duration-200
                  hover:border-[var(--color-secondary)]
                "
                >
                  <div
                    className="
                    text-[var(--color-footer-02)]
                    transition duration-200
                    group-hover:text-[var(--color-secondary)]
                  "
                  >
                    {s.icon}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Link Sections */}
          <div className="flex flex-col sm:flex-row justify-between sm:justify-around lg:justify-end lg:w-[65%] gap-8 sm:gap-6 lg:gap-12">
            {footerLinks.map((section, idx) => (
              <div key={idx} className="flex flex-col gap-2 min-w-[120px]">
                <h4
                  className="mb-4 font-semibold text-footer-level-1"
                  style={{ color: "var(--color-footer-01)" }}
                >
                  {section.title}
                </h4>
                {section.links.map((link, i) => (
                  <span
                    key={i}
                    className="mb-2 cursor-pointer text-footer-level-1 hover:text-[var(--color-secondary)] transition-colors"
                    style={{ color: "var(--color-footer-01)" }}
                    onClick={() => handleNavigation(link.path)}
                  >
                    {link.name}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <hr
          className="w-full mt-10 lg:mt-12"
          style={{ borderColor: "var(--color-stroke-01)" }}
        />

        {/* Footer bottom */}
        <div
          className="flex justify-center py-4 lg:py-6 text-center text-sm text-gray-500"
          style={{ color: "var(--color-footer-03)" }}
        >
          Icieos © {currentYear}. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Footer;
